import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import Icon from "@web-ui/components/Icon";
import { BRANDS, type Brand } from "@/api/client";
import { BrandMark } from "@/components/brand-mark";
import { href } from "@/lib/route";

/**
 * The brand switcher at the top of the sidebar (Shopify's store picker): the brand being edited,
 * with its mark and name, and a menu of every brand. Signing in is per brand, so a brand you are
 * not signed in to opens its sign-in.
 */
export function BrandSwitcher({ current, signedIn }: { current: Brand; signedIn: (id: string) => boolean | undefined }) {
  return (
    <Menu as="div" className="relative">
      <MenuButton className="flex w-full items-center gap-3 rounded-2xl p-1.5 text-start transition-colors hover:bg-b-surface2 focus-visible:outline-2 focus-visible:outline-s-focus data-[open]:bg-b-surface2" aria-label={`${current.name}: switch brand`}>
        <BrandMark brand={current} current size="lg" />
        <span className="min-w-0 grow">
          <span className="block truncate text-sub-title-1 text-t-primary">{current.name}</span>
          <span className="block truncate text-caption text-t-tertiary">Studio Console</span>
        </span>
        <Icon name="chevron" size={5} className="shrink-0 fill-t-secondary" />
      </MenuButton>
      <MenuItems className="menu w-72 max-md:w-[calc(100vw-2rem)]" anchor="bottom start" transition>
        <div className="px-3 pt-1 pb-2 text-caption text-t-tertiary">Your brands</div>
        {BRANDS.map((b) => {
          const state = signedIn(b.id);
          return (
            <MenuItem key={b.id}>
              <a className="menu__item gap-3 !h-auto py-2" href={href({ brand: b.id, section: "home" })} aria-current={b.id === current.id ? "true" : undefined}>
                <BrandMark brand={b} current={b.id === current.id} size="sm" />
                <span className="min-w-0 grow">
                  <span className="block truncate text-body-2 text-t-primary">{b.name}</span>
                  <span className="block truncate text-caption text-t-tertiary">{b.id === current.id ? "Open now" : state ? "Signed in" : "Sign in to open"}</span>
                </span>
                {b.id === current.id ? <Icon name="check" size={5} className="fill-t-primary" /> : null}
              </a>
            </MenuItem>
          );
        })}
      </MenuItems>
    </Menu>
  );
}
